import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';

export default function FontSelect({ fonts, value, onChange }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  const selected = fonts.find(f => f.id === value);

  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    function handleKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  // Scroll selected item into view when dropdown opens
  useEffect(() => {
    if (open && listRef.current) {
      const active = listRef.current.querySelector('[data-active="true"]');
      if (active) active.scrollIntoView({ block: 'nearest' });
    }
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white text-left focus:ring-2 focus:ring-bastion-black focus:border-bastion-black"
      >
        <span
          className="truncate text-base"
          style={{ fontFamily: `'${value}', cursive` }}
        >
          {selected ? selected.name : 'Select font'}
        </span>
        <ChevronDown className={`w-4 h-4 ml-2 shrink-0 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <ul
          ref={listRef}
          className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-lg border border-gray-300 bg-white shadow-lg"
        >
          {fonts.map(font => {
            const isSelected = font.id === value;
            return (
              <li
                key={font.id}
                data-active={isSelected}
                onClick={() => { onChange(font.id); setOpen(false); }}
                className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-100 ${isSelected ? 'bg-gray-50' : ''}`}
              >
                <Check className={`w-4 h-4 shrink-0 ${isSelected ? 'text-bastion-black' : 'text-transparent'}`} />
                <span
                  className="text-base"
                  style={{ fontFamily: `'${font.id}', cursive` }}
                >
                  {font.name}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
